import React, {useCallback, useEffect, useRef, useState} from 'react'
import './Messages.scss'

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@awesome.me/kit-a13c95776b/icons/modules/sharp/thin";
import {useDispatch, useSelector} from "react-redux";
import {
    addLastActive,
    changeMessageFilter,
    requestMessages,
    requestMoreMessages,
    requestConversations,
    updateConversation
} from "../../../actions/direct_messages";
import {formatDistanceToNow} from "date-fns";
import MessagesReply from "./MessagesReply";
import {faBoxArchive, faThumbsUp} from "@fortawesome/free-solid-svg-icons";
import Button from "../Button";
import ReactTooltip from "react-tooltip";
import Item from "./Item";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import FacebookLoginButton from "../FacebookLoginButton";

const Messages = () => {
    const dispatch = useDispatch()
    const {details} = useSelector(state => state.pages.facebook.activePage)
    const {conversations, messagesList, lastActive, filters, unarchivedConvosCount} = useSelector(state => state.direct_messages)
    const {bottom} = filters
    const {archived} = bottom
    const [showChat, setShowChat] = useState(false)

    const listRef = useRef(null)
    const isPrependingRef = useRef(false)
    const loadingRef = useRef(false)
    const lastBeforeRef = useRef(null)
    const throttleRef = useRef(0)

    const [isLoadingMsgs, setIsLoadingMsgs] = useState(false)
    const [hasMoreMsgs, setHasMoreMsgs] = useState(true)

    useEffect(() => {
        dispatch(requestConversations())
    }, [filters, details, dispatch])


    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        if (isPrependingRef.current) {
            isPrependingRef.current = false;
            return;
        }
        list.scrollTop = list.scrollHeight;
    }, [messagesList])

    const reload = () => {
        conversations.length > 0 && !lastActive && loadMessages(conversations[0].attributes.last_message)
    }
    useEffect(() => {
        reload()
        setHasMoreMsgs(true)
        loadingRef.current = false
        lastBeforeRef.current = null
    }, [conversations])

    const loadMessages = (m, show=false) => {
        dispatch(addLastActive(m))
        dispatch(requestMessages(m.id)).then((res) => {
            if (typeof res?.hasMore === 'boolean') setHasMoreMsgs(res.hasMore)
            lastBeforeRef.current = null
        })
        setShowChat(show)
    }

    const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent)

    const convoUpdate = (name) => {
        if (!lastActive) return
        dispatch(addLastActive())
        dispatch(updateConversation(lastActive.id, name))
    }

    const truncateString = (string = '', maxLength = 50) =>
        string.length > maxLength ? `${string.substring(0, maxLength)}…` : string

    const setUnreview = (index) => {
        // 0 = Inbox, 1 = Archive
        dispatch(addLastActive())
        dispatch(changeMessageFilter(false, "archived", index !== 0))
    }

    const loadMoreMessages = useCallback(async () => {
        const listEl = listRef.current
        if (!lastActive || !messagesList?.length || !listEl) return
        if (!hasMoreMsgs || loadingRef.current) return

        if (listEl.scrollTop > 40 || listEl.scrollTop < 0) return

        const now = Date.now()
        if (now - throttleRef.current < 150) return
        throttleRef.current = now

        const before = messagesList[0]?.id
        if (!before) return

        if (before === lastBeforeRef.current) return
        lastBeforeRef.current = before

        const prevScrollHeight = listEl.scrollHeight
        const prevScrollTop = listEl.scrollTop

        loadingRef.current = true
        isPrependingRef.current = true
        setIsLoadingMsgs(true)

        try {
            const res = await dispatch(requestMoreMessages(lastActive.id, { before }))
            if (!res || res.count === 0) {
                setHasMoreMsgs(false)
                isPrependingRef.current = false
                return
            }

            requestAnimationFrame(() => {
                const newScrollHeight = listEl.scrollHeight
                listEl.scrollTop = (newScrollHeight - prevScrollHeight) + prevScrollTop
            })
        } finally {
            loadingRef.current = false
            setIsLoadingMsgs(false)
        }
    }, [dispatch, lastActive, messagesList, hasMoreMsgs])

    const onScroll = useCallback(() => {
        const el = listRef.current
        if (!el) return
        if (el.scrollTop <= 40) loadMoreMessages()
    }, [loadMoreMessages])

    useEffect(() => {
        setHasMoreMsgs(true)
        loadingRef.current = false
        lastBeforeRef.current = null
    }, [lastActive?.id])

    return (
        <>
            <div className="mt-4 mb-3">
                {details.attributes.has_messages_scope ?
                    <>Here you'll find an overview of the messages you've received.</>
                    : <>To add messaging functionality, an additional permission is needed.
                        <div className="my-4">
                            <FacebookLoginButton href="/auth/facebook?scope=instagram_manage_messages&redirect=/" text="Activate messaging"/>
                        </div>
                    </>}
            </div>

            {details.attributes.has_messages_scope &&
            <div className={`msg-wrapper ${showChat && 'showChat'}`}>

                <div className="msg-inbox">
                    <div className="msg-header msg-tabs">
                        <Tabs onSelect={setUnreview}>
                            <TabList className="react-tabs__tab-list filters">
                                <Tab>
                                    Inbox
                                    {unarchivedConvosCount > 0 && <span className="comments-counter px-1">{unarchivedConvosCount}</span>}
                                </Tab>
                                <Tab>Archive</Tab>
                            </TabList>

                            <TabPanel />
                            <TabPanel />
                        </Tabs>
                    </div>

                    <div className="msg-inbox-w">
                        <div className="sender-list">
                            {conversations?.length > 0 ? conversations.map((c) => {
                                return <div
                                    className={`msg-inbox-item ${lastActive?.attributes?.sender_id === c.attributes.last_message.attributes.sender_id && 'selected'}`}
                                    key={c.id}
                                    onClick={() => loadMessages(c.attributes.last_message, true)}
                                >
                                    <div className="name-time">
                                        <div className="name">
                                            {c.attributes.last_message.attributes.sender_name}
                                        </div>
                                        <div className="time">
                                            {c.attributes.last_message_at && formatDistanceToNow(new Date(c.attributes.last_message_at), {addSuffix: true})}
                                        </div>
                                    </div>
                                    <div className="last-msg">
                                        {c.attributes.last_message.attributes.attachment_type === "like"
                                            ? <FontAwesomeIcon icon={faThumbsUp} className="fa-fw"/>
                                            : truncateString(c.attributes.last_message.attributes?.message) || `[${c.attributes.last_message.attributes?.attachment_type}]`}
                                    </div>
                                </div>
                            }) : <div className="no-conv"> No conversations</div>}
                        </div>
                    </div>
                </div>

                <div className="msg-list">
                    <div className="msg-header">
                        <div className="arrow" onClick={() => setShowChat(false)}>
                            <FontAwesomeIcon icon={faArrowLeft} className="fa-fw"/>
                        </div>
                        <div className="text">{lastActive && lastActive.attributes.sender_name}</div>

                        <div className="btns">
                            <Button
                                className={`square-btn ${archived ? 'active' : ''}`}
                                clickHandler={()=>convoUpdate("archived")}
                                dataTip={isMobileDevice ? '' : (archived ? 'Unarchive' : 'Archive')}
                            >
                                <FontAwesomeIcon icon={faBoxArchive} className="fa-fw"/>
                            </Button>
                        </div>
                    </div>

                    <div className="msg-list-w">
                        <div
                            className="list"
                            id="list"
                            ref={listRef}
                            onScroll={onScroll}   // <= gate loads here
                        >
                            {/* No sentinel needed */}
                            {messagesList && messagesList.map((m) => (
                                <Item dm={m} key={m.id} pageId={details.id}/>
                            ))}
                        </div>

                        <div className="control-area">
                            {conversations?.length > 0 && lastActive && <MessagesReply selectedMessage={lastActive} pageId={details.id}/>}
                        </div>

                        {isLoadingMsgs && (
                            <div className="loading more-messages">Loading older messages…</div>
                        )}
                        {!hasMoreMsgs && (
                            <div className="no-more-messages">No more messages</div>
                        )}
                    </div>
                </div>
            </div>
            }
            <ReactTooltip effect="solid" html={false} className="switch-tooltip"/>
        </>
    )
}

export default React.memo(Messages)
